import axiosClient, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { AuthHandler, JsonWebToken } from '@steelbuy/auth';
import { reportRequestError } from '@steelbuy/error';
import { getBaseUrl, getTenant } from './utils/getBaseUrl';

const JSON_WEB_TOKEN_STORAGE_KEY = 'lib_auth_jwt';

export type LoginSuccessResponse = {
    accessToken: string;
    refreshToken: string;
};

const baseInstance = axiosClient.create({
    baseURL: getBaseUrl(),
    headers: {
        Accept: 'application/json',
    },
});

export { baseInstance as unauthenticatedAxios };

export const instance = axiosClient.create({
    baseURL: getBaseUrl(),
    headers: {
        Accept: 'application/json',
    },
});

const getTokenFromLocal = () => {
    const tokenData = localStorage.getItem(`steelbuy_${JSON_WEB_TOKEN_STORAGE_KEY}`);
    if (tokenData) {
        const tokenObj: JsonWebToken = JSON.parse(tokenData);
        return tokenObj;
    }
    window.location.assign('login');
    throw Error('Unautenticated');
};

const refreshToken = async (refreshTokenStr: string) => {
    const { userId } = getTokenFromLocal();
    const { data } = await baseInstance.post<LoginSuccessResponse>(`/api/auth/refresh-token`, {
        refreshToken: refreshTokenStr,
        tenant: getTenant(),
        userId,
    });
    return data;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const needsUpdate = (token: any): boolean => {
    if (!token) {
        return false;
    }
    const validToTimestamp = token.accessTokenValidTo ?? 0;
    return validToTimestamp < Date.now() + 3000;
};

export const getToken = async (): Promise<string> => {
    const tokenObj = getTokenFromLocal();
    if (needsUpdate(tokenObj)) {
        // TODO Look into replacing AuthHandler
        const authHandler = AuthHandler.get();
        try {
            if (!authHandler.getIsFetchingToken()) {
                authHandler.setIsFetchingToken(true);
                const newToken = await refreshToken(tokenObj.refreshToken);
                authHandler.update(newToken.accessToken);
                return newToken.accessToken;
            }
            await authHandler.waitForToken();
            const updatedToken = getTokenFromLocal();
            if (updatedToken.accessToken) {
                return updatedToken.accessToken;
            }
            throw new Error('Invalid Token');
        } catch (e) {
            authHandler.unauthenticate();
        } finally {
            authHandler.setIsFetchingToken(false);
        }
    }
    if (tokenObj.accessToken) {
        return tokenObj.accessToken;
    }
    window.location.assign('login');
    // TODO MAKE THIS BETTER
    throw Error('Unautenticated');
};

instance.interceptors.request.use(async (axiosConfig: InternalAxiosRequestConfig) => {
    const config = axiosConfig;
    const accessToken = await getToken();
    if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
});

instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        if (error.config) {
            reportRequestError(error, error.config.method || 'unknown method', error.config.url || 'unkown url');
        }
        if (error.status === 401) {
            AuthHandler.get().unauthenticate();
            window.location.assign('login');
        }
        return Promise.reject(error);
    }
);

export default instance;
